import { onAnimationEnd } from '@theme/utilities';

class SizeGuideDialog extends HTMLElement {
  #previousScrollY = 0;

  connectedCallback() {
    this.dialog = this.querySelector('[data-size-guide-dialog]');
    this.closeButton = this.querySelector('[data-size-guide-close]');
    this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));

    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.#bindTriggers();
    this.#formatValues();
    this.#setActiveUnit(this.dataset.defaultUnit);

    this.closeButton?.addEventListener('click', this.#handleCloseClick);
    this.dialog.addEventListener('click', this.#handleDialogClick);
    this.dialog.addEventListener('cancel', this.#handleDialogCancel);

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', this.#handleUnitClick);
    });
  }

  disconnectedCallback() {
    this.#unbindTriggers();
    this.closeButton?.removeEventListener('click', this.#handleCloseClick);
    this.dialog?.removeEventListener('click', this.#handleDialogClick);
    this.dialog?.removeEventListener('cancel', this.#handleDialogCancel);

    this.unitButtons?.forEach((button) => {
      button.removeEventListener('click', this.#handleUnitClick);
    });
  }

  #triggerButtons = [];

  #bindTriggers() {
    this.#triggerButtons = Array.from(document.querySelectorAll(`[data-size-guide-trigger="${this.id}"]`));

    this.#triggerButtons.forEach((button) => {
      button.addEventListener('pointerdown', this.#handleTriggerPointerDown, { capture: true });
      button.addEventListener('click', this.#handleTriggerClick, { capture: true });
    });
  }

  #unbindTriggers() {
    this.#triggerButtons.forEach((button) => {
      button.removeEventListener('pointerdown', this.#handleTriggerPointerDown, { capture: true });
      button.removeEventListener('click', this.#handleTriggerClick, { capture: true });
    });

    this.#triggerButtons = [];
  }

  #cancelEvent(event) {
    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  }

  #handleTriggerPointerDown = (event) => {
    this.#cancelEvent(event);
  };

  #handleTriggerClick = (event) => {
    this.#cancelEvent(event);
    this.show();
  };

  #handleCloseClick = (event) => {
    this.#cancelEvent(event);
    this.close();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.dialog) {
      this.close();
    }
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handleUnitClick = (event) => {
    this.#cancelEvent(event);

    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    this.#setActiveUnit(button.dataset.sizeGuideUnit);
  };

  #setActiveUnit(unit) {
    const nextUnit = unit === 'in' ? 'in' : 'cm';
    this.dataset.activeUnit = nextUnit;

    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.sizeGuideUnit === nextUnit ? 'true' : 'false');
    });
  }

  #formatValues() {
    this.querySelectorAll('[data-size-guide-value]').forEach((value) => {
      if (!(value instanceof HTMLElement)) return;

      const rawValue = Number.parseFloat(value.dataset.sizeGuideRaw || value.textContent || '');
      if (!Number.isFinite(rawValue)) return;

      const precision = value.dataset.sizeGuideValue === 'in' ? 1 : 0;
      value.textContent = rawValue.toFixed(precision);
    });
  }

  show() {
    if (!(this.dialog instanceof HTMLDialogElement) || this.dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      this.dialog.showModal();
    });
  }

  close = async () => {
    if (!(this.dialog instanceof HTMLDialogElement) || !this.dialog.open) return;

    this.dialog.style.animation = 'none';
    void this.dialog.offsetWidth;
    this.dialog.classList.add('dialog-closing');
    this.dialog.style.animation = '';

    await onAnimationEnd(this.dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.dialog.close();
    this.dialog.classList.remove('dialog-closing');
  };
}

if (!customElements.get('size-guide-dialog')) {
  customElements.define('size-guide-dialog', SizeGuideDialog);
}
